'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export interface FormData {
  name: string;
  phone: string;
  /** Optional email; shown below phone on checkout */
  email?: string;
  address: string;
  specialInstructions: string;
  deliveryOption: 'delivery' | 'pickup';
  paymentMethod: 'cash' | 'online';
  /** Set when user uses "Use Current Location"; sent to API for 3 km validation */
  deliveryLat?: number;
  deliveryLng?: number;
}

interface FormErrors {
  name?: string;
  phone?: string;
  address?: string;
}

interface CheckoutFormProps {
  onSubmit: (data: FormData) => void;
  isProcessing: boolean;
}

const CheckoutForm = ({ onSubmit, isProcessing }: CheckoutFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    address: '',
    specialInstructions: '',
    deliveryOption: 'delivery',
    paymentMethod: 'cash',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLocating, setIsLocating] = useState(false);

  const DELIVERY_RADIUS_KM = 3;
  function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrors((prev) => ({ ...prev, address: 'Geolocation is not supported by your browser.' }));
      return;
    }
    setIsLocating(true);
    setErrors((prev) => ({ ...prev, address: undefined }));
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { Accept: 'application/json' } }
          );
          const data = await res.json();
          const address = data?.address
            ? [
                data.address.road,
                data.address.suburb || data.address.neighbourhood,
                data.address.city || data.address.town || data.address.village,
                data.address.state,
                data.address.postcode,
                data.address.country,
              ]
                .filter(Boolean)
                .join(', ') || data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
            : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          const locsRes = await fetch('/api/delivery-locations');
          const locs: { name: string; latitude: number; longitude: number }[] = (await locsRes.json()) || [];
          const locsWithNumbers = locs.map((loc) => ({
            name: loc.name || 'Our location',
            latitude: Number(loc.latitude),
            longitude: Number(loc.longitude),
          }));
          let nearestDistKm: number | null = null;
          let nearestName: string | null = null;
          for (const loc of locsWithNumbers) {
            const d = haversineKm(lat, lng, loc.latitude, loc.longitude);
            if (nearestDistKm === null || d < nearestDistKm) {
              nearestDistKm = d;
              nearestName = loc.name;
            }
          }
          const withinRange =
            locsWithNumbers.length === 0 ||
            (nearestDistKm !== null && nearestDistKm <= DELIVERY_RADIUS_KM);
          if (!withinRange && locsWithNumbers.length > 0 && nearestDistKm !== null && nearestName) {
            const distText = nearestDistKm.toFixed(1);
            setErrors((prev) => ({
              ...prev,
              address: `Your current location is ${distText} km from "${nearestName}". We deliver only within 3 km of our locations.`,
            }));
            setTouched((prev) => ({ ...prev, address: true }));
            setIsLocating(false);
            return;
          }
          setFormData((prev) => ({
            ...prev,
            address,
            deliveryLat: lat,
            deliveryLng: lng,
          }));
          setErrors((prev) => ({ ...prev, address: undefined }));
        } catch (e) {
          setErrors((prev) => ({ ...prev, address: 'Could not get address. Please enter it manually.' }));
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setErrors((prev) => ({
          ...prev,
          address: 'Unable to get your location. Please allow location access or enter address manually.',
        }));
        setTouched((prev) => ({ ...prev, address: true }));
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'phone': {
        if (!value.trim()) return 'Phone number is required';
        const phoneRegex = /^\+?1?\s*\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
        if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
        return undefined;
      }
      case 'address':
        if (formData.deliveryOption === 'delivery' && !value.trim())
          return 'Delivery address is required';
        if (formData.deliveryOption === 'delivery' && value.trim().length < 10)
          return 'Please enter a complete address';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value } as FormData;
      if (name === 'address') {
        next.deliveryLat = undefined;
        next.deliveryLng = undefined;
      }
      return next;
    });

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleDeliveryOptionChange = (option: 'delivery' | 'pickup') => {
    setFormData((prev) => ({ ...prev, deliveryOption: option }));
    if (option === 'pickup') {
      setErrors((prev) => ({ ...prev, address: undefined }));
    }
  };

  const handlePaymentMethodChange = (method: 'cash' | 'online') => {
    setFormData((prev) => ({ ...prev, paymentMethod: method }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    newErrors.name = validateField('name', formData.name);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.address = validateField('address', formData.address);

    const hasErrors = Object.values(newErrors).some((error) => error !== undefined);

    if (hasErrors) {
      setErrors(newErrors);
      setTouched({ name: true, phone: true, address: true });
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg bg-card shadow-md">
      <div className="border-b border-border px-6 py-4">
        <h2 className="font-heading text-xl font-semibold text-foreground">Checkout Information</h2>
      </div>

      <div className="space-y-6 px-6 py-6">
        {/* Customer Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Customer Details</h3>

          <div>
            <label htmlFor="name" className="text-caption mb-2 block text-foreground">
              Full Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
                errors.name && touched.name
                  ? 'border-error bg-error/5'
                  : 'border-border bg-background'
              }`}
              placeholder="Durga Shankar"
              disabled={isProcessing}
            />
            {errors.name && touched.name && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.name}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="text-caption mb-2 block text-foreground">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
                errors.phone && touched.phone
                  ? 'border-error bg-error/5'
                  : 'border-border bg-background'
              }`}
              placeholder="+91 75420 72555"
              disabled={isProcessing}
            />
            {errors.phone && touched.phone && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="text-caption mb-2 block text-foreground">
              Email <span className="text-muted-foreground text-xs">(optional)</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm transition-smooth focus-ring"
              placeholder="you@example.com"
              disabled={isProcessing}
            />
          </div>
        </div>

        {/* Delivery Options */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Delivery Option</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handleDeliveryOptionChange('delivery')}
              disabled={isProcessing}
              className={`flex items-center space-x-3 rounded-lg border p-4 transition-smooth focus-ring ${
                formData.deliveryOption === 'delivery'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              <Icon
                name="TruckIcon"
                size={24}
                className={
                  formData.deliveryOption === 'delivery' ? 'text-primary' : 'text-muted-foreground'
                }
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Delivery</p>
                <p className="text-caption text-muted-foreground">30-45 mins • ₹5.99</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleDeliveryOptionChange('pickup')}
              disabled={isProcessing}
              className={`flex items-center space-x-3 rounded-lg border p-4 transition-smooth focus-ring ${
                formData.deliveryOption === 'pickup'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              <Icon
                name="ShoppingBagIcon"
                size={24}
                className={
                  formData.deliveryOption === 'pickup' ? 'text-primary' : 'text-muted-foreground'
                }
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Pickup</p>
                <p className="text-caption text-muted-foreground">15-20 mins • Free</p>
              </div>
            </button>
          </div>
        </div>

        {/* Delivery Address */}
        {formData.deliveryOption === 'delivery' && (
          <div>
            <label htmlFor="address" className="text-caption mb-2 block text-foreground">
              Delivery Address <span className="text-error">*</span>
            </label>
            <div className="relative">
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={3}
                className={`w-full rounded-lg border px-4 py-3 pr-12 text-sm transition-smooth focus-ring ${
                  errors.address && touched.address
                    ? 'border-error bg-error/5'
                    : 'border-border bg-background'
                }`}
                placeholder="123 Main Street, Apt 4B, New York, NY 10001"
                disabled={isProcessing}
              />
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isProcessing || isLocating}
                className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground shadow-sm transition-smooth hover:bg-muted focus-ring disabled:opacity-50"
                title="Use current location (we deliver within 3 km of our locations)"
              >
                <span className="text-base" aria-hidden>📍</span>
                {isLocating ? (
                  <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                ) : (
                  <span>Use Current Location</span>
                )}
              </button>
            </div>
            {errors.address && touched.address && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.address}</span>
              </p>
            )}
          </div>
        )}

        {/* Special Instructions */}
        <div>
          <label htmlFor="specialInstructions" className="text-caption mb-2 block text-foreground">
            Special Instructions (Optional)
          </label>
          <textarea
            id="specialInstructions"
            name="specialInstructions"
            value={formData.specialInstructions}
            onChange={handleChange}
            rows={3}
            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm transition-smooth focus-ring"
            placeholder="Extra spicy, no onions, ring doorbell twice..."
            disabled={isProcessing}
          />
        </div>

        {/* Payment Method */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Payment Method</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handlePaymentMethodChange('cash')}
              disabled={isProcessing}
              className={`flex items-center space-x-3 rounded-lg border p-4 transition-smooth focus-ring ${
                formData.paymentMethod === 'cash'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              <Icon
                name="BanknotesIcon"
                size={24}
                className={
                  formData.paymentMethod === 'cash' ? 'text-primary' : 'text-muted-foreground'
                }
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Cash on Delivery</p>
                <p className="text-caption text-muted-foreground">Pay when you receive</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handlePaymentMethodChange('online')}
              disabled={isProcessing}
              className={`flex items-center space-x-3 rounded-lg border p-4 transition-smooth focus-ring ${
                formData.paymentMethod === 'online'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              <Icon
                name="CreditCardIcon"
                size={24}
                className={
                  formData.paymentMethod === 'online' ? 'text-primary' : 'text-muted-foreground'
                }
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">Online Payment</p>
                <p className="text-caption text-muted-foreground">Secure checkout</p>
              </div>
            </button>
          </div>
        </div>

        {/* Security Badge */}
        <div className="flex items-center justify-center space-x-2 rounded-lg bg-success/10 px-4 py-3">
          <Icon name="ShieldCheckIcon" size={20} className="text-success" />
          <p className="text-caption text-success">
            Your payment information is secure and encrypted
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isProcessing}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-accent px-6 py-4 text-base font-semibold text-accent-foreground shadow-md transition-smooth hover-lift active-press focus-ring hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isProcessing ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Processing Order...</span>
            </>
          ) : (
            <>
              <Icon name="CheckCircleIcon" size={20} />
              <span>Place Order</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CheckoutForm;
